import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronDown, ChevronRight, FolderOpen, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { dashboardToast } from "../../utils/dashboardToast";
import Menu from "../components/menu";
import DashboardShell from "../../components/DashboardShell";
import { useMenuState } from "../../Common/MenuStateContext";
import SaListPageShell from "../../components/sa/SaListPageShell";
import ContentLoader from "../../components/ContentLoader";
import RecordDetailModal from "../../components/RecordDetailModal";
import TableSortSelect from "../../components/TableSortSelect";
import { Button, Checkbox, Input, Modal, Select } from "../../components/ui";
import { TABLE_SORT, sortTableRows } from "../../utils/tableSort";
import { getCompanyList } from "../../services/companyApi";
import { getProjectList, addOrUpdateProject, deleteProject } from "../../services/projectApi";
import { getSMEList } from "../../services/smeApi";
import { getDepartmentNames } from "../../services/departmentApi";
import ProjectInvitationsPanel from "../components/ProjectInvitationsPanel";
import "../../assets/css/dashboard.css";

const getProjectAssessmentBlueprintTitle = (project) =>
  project?.assessmentBlueprintTitle ?? project?.AssessmentBlueprintTitle ?? null;

const getProjectAssessmentBlueprintId = (project) => {
  const rawId = project?.assessmentBlueprintId ?? project?.AssessmentBlueprintId;
  const id = Number(rawId);
  return Number.isFinite(id) && id > 0 ? id : null;
};

const projectHasAssessmentBlueprint = (project) =>
  Boolean(getProjectAssessmentBlueprintId(project));

const getProjectSmeUsers = (project) => {
  if (!project) return [];
  const users = project.smeUsers ?? project.SMEUsers ?? [];
  return Array.isArray(users) ? users : [];
};

const resolveProjectSmeUserIds = (project) =>
  getProjectSmeUsers(project)
    .map((user) => Number(user.id ?? user.Id ?? user.userId ?? user.UserId))
    .filter((id) => !Number.isNaN(id));

const getSmeDisplayName = (user) =>
  user?.name ?? user?.Name ?? user?.fullName ?? user?.FullName ?? "Unknown";

const emptyForm = {
  companyId: "",
  projectName: "",
  departmentName: "",
  projectCreationDate: "",
  smeUserIds: [],
};

const ProjectManagement = () => {
  const { isMenuCollapsed, toggleMenuCollapse } = useMenuState();
  const [searchParams] = useSearchParams();

  const [visibleSection, setVisibleSection] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [projects, setProjects] = useState({});
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [loadingProjects, setLoadingProjects] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [smes, setSmes] = useState([]);
  const [loadingSMEs, setLoadingSMEs] = useState(false);
  const [smeSearch, setSmeSearch] = useState("");
  const [departmentNames, setDepartmentNames] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [invitationProject, setInvitationProject] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewProject, setViewProject] = useState(null);
  const [sortKey, setSortKey] = useState(TABLE_SORT.DATE_DESC);
  const [searchTerm, setSearchTerm] = useState("");

  const handleMenuSelection = (item) => {
    setSelectedItem(item);
  };

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoadingCompanies(true);
        const response = await getCompanyList(1, 100);
        const companyData = response.data ? response.data.items : response.items || [];
        setCompanies(companyData);
      } catch (error) {
        console.error("Error fetching companies:", error);
        setCompanies([]);
      } finally {
        setLoadingCompanies(false);
      }
    };

    fetchCompanies();
  }, []);

  useEffect(() => {
    const companyIdParam = searchParams.get("companyId");
    if (!companyIdParam || companies.length === 0) return;

    const companyId = Number(companyIdParam);
    if (Number.isNaN(companyId)) return;

    setVisibleSection(companyId);
    setSelectedCard(companyId);
    fetchProjectsForCompany(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, searchParams]);

  useEffect(() => {
    const fetchSMEs = async () => {
      try {
        setLoadingSMEs(true);
        const res = await getSMEList();
        setSmes(res ?? []);
      } catch (err) {
        console.error(err);
        setSmes([]);
      } finally {
        setLoadingSMEs(false);
      }
    };
    fetchSMEs();
  }, []);

  // Refresh on every open so departments added in Settings show up without a reload.
  useEffect(() => {
    if (!showModal) return undefined;
    let cancelled = false;
    const fetchDepartments = async () => {
      try {
        setLoadingDepartments(true);
        const names = await getDepartmentNames();
        if (!cancelled) setDepartmentNames(names);
      } catch (err) {
        console.error(err);
        if (!cancelled) setDepartmentNames([]);
      } finally {
        if (!cancelled) setLoadingDepartments(false);
      }
    };
    fetchDepartments();
    return () => {
      cancelled = true;
    };
  }, [showModal]);

  const handleSmeToggle = (smeId, checked) => {
    const id = Number(smeId);
    setFormData((prev) => {
      const currentIds = prev.smeUserIds.map(Number);
      const nextIds = checked
        ? currentIds.includes(id)
          ? currentIds
          : [...currentIds, id]
        : currentIds.filter((existingId) => existingId !== id);

      return { ...prev, smeUserIds: nextIds };
    });
    setFormErrors((prev) => ({ ...prev, smeUserIds: "" }));
  };

  const filteredSmes = smes.filter((sme) => {
    const q = smeSearch.trim().toLowerCase();
    if (!q) return true;
    return `${sme.name || ""} ${sme.designation || ""}`.toLowerCase().includes(q);
  });

  const isSmeSelected = (smeId) =>
    formData.smeUserIds.some((id) => Number(id) === Number(smeId));

  const fetchProjectsForCompany = async (companyId) => {
    try {
      setLoadingProjects((prev) => ({ ...prev, [companyId]: true }));
      const projectData = await getProjectList(companyId);
      setProjects((prev) => ({
        ...prev,
        [companyId]: projectData,
      }));
    } catch (error) {
      console.error("Error fetching projects for company:", companyId, error);
      setProjects((prev) => ({
        ...prev,
        [companyId]: [],
      }));
    } finally {
      setLoadingProjects((prev) => ({ ...prev, [companyId]: false }));
    }
  };

  const toggleVisibility = (companyId) => {
    if (visibleSection === companyId) {
      setVisibleSection(null);
      setSelectedCard(null);
      return;
    }
    setVisibleSection(companyId);
    setSelectedCard(companyId);
    fetchProjectsForCompany(companyId);
  };

  const getProjectsForCompany = (companyId) => projects[companyId] || [];

  const closeViewModal = () => {
    setShowViewModal(false);
    setViewProject(null);
  };

  const handleProjectClick = (project) => {
    localStorage.setItem("selectedProjectId", project.id);
    setViewProject(project);
    setShowViewModal(true);
  };

  const syntheticStopEvent = { stopPropagation: () => {} };

  const openInvitations = (project, event) => {
    event.stopPropagation();
    if (!projectHasAssessmentBlueprint(project)) return;
    setInvitationProject(project);
    setShowInvitationModal(true);
  };

  const openModal = () => {
    setSmeSearch("");
    setIsEditMode(false);
    setEditingProject(null);
    setShowModal(true);
    const today = new Date().toISOString().split("T")[0];
    setFormData({
      ...emptyForm,
      companyId: visibleSection ? String(visibleSection) : "",
      projectCreationDate: today,
    });
    setFormErrors({});
  };

  const openEditModal = (project, event) => {
    event.stopPropagation();
    setIsEditMode(true);
    setEditingProject(project);
    setSmeSearch("");
    setShowModal(true);
    setFormData({
      companyId: project.companyId || "",
      projectName: project.projectName || "",
      departmentName: project.departmentName || "",
      projectCreationDate: project.projectCreationDate
        ? new Date(project.projectCreationDate).toISOString().split("T")[0]
        : "",
      smeUserIds: resolveProjectSmeUserIds(project),
    });
    setFormErrors({});
  };

  const openDeleteModal = (project, event) => {
    event.stopPropagation();
    setProjectToDelete(project);
    setShowDeleteModal(true);
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setProjectToDelete(null);
  };

  const confirmDelete = async () => {
    if (!projectToDelete) return;

    const deleteId = projectToDelete.id ?? projectToDelete.Id;
    const companyId =
      projectToDelete.companyId ?? projectToDelete.CompanyId ?? selectedCard;

    if (!deleteId) {
      dashboardToast.error("Invalid project ID");
      return;
    }

    try {
      setIsDeleting(true);
      await deleteProject(deleteId);
      dashboardToast.success("Project deleted successfully");
      setShowDeleteModal(false);
      setProjectToDelete(null);
      if (companyId) {
        await fetchProjectsForCompany(companyId);
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to delete project. Please try again.";
      dashboardToast.error(message);
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    if (!showModal || !isEditMode || !editingProject) return;

    const resolvedIds = resolveProjectSmeUserIds(editingProject);
    setFormData((prev) => {
      const sameLength = prev.smeUserIds.length === resolvedIds.length;
      const sameIds =
        sameLength && prev.smeUserIds.every((id) => resolvedIds.includes(Number(id)));

      return sameIds ? prev : { ...prev, smeUserIds: resolvedIds };
    });
  }, [showModal, isEditMode, editingProject]);

  const closeModal = () => {
    setSmeSearch("");
    setShowModal(false);
    setIsEditMode(false);
    setEditingProject(null);
    setFormData(emptyForm);
    setFormErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const setFormField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.companyId) errors.companyId = "Company is required";
    if (!formData.projectName.trim()) errors.projectName = "Project name is required";
    if (!formData.departmentName.trim()) errors.departmentName = "Department name is required";
    if (!formData.projectCreationDate) {
      errors.projectCreationDate = "Project creation date is required";
    }

    const assigningSmes = formData.smeUserIds.length > 0;
    if (assigningSmes && isEditMode && !projectHasAssessmentBlueprint(editingProject)) {
      errors.smeUserIds =
        "Publish an assessment to this project before assigning SMEs.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const payload = {
        projectId: isEditMode ? editingProject.id : 0,
        companyId: Number(formData.companyId),
        projectName: formData.projectName.trim(),
        departmentName: formData.departmentName.trim(),
        projectCreationDate: formData.projectCreationDate,
        smeUserIds: formData.smeUserIds,
      };

      await addOrUpdateProject(payload);
      await fetchProjectsForCompany(payload.companyId);
      dashboardToast.success(isEditMode ? "Project updated." : "Project created.");
      closeModal();
    } catch (error) {
      console.error("Save failed:", error);
      dashboardToast.error(
        `Error ${isEditMode ? "updating" : "adding"} project. Please try again.`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingCompanies) {
    return (
      <DashboardShell
        isMenuCollapsed={isMenuCollapsed}
        sidebar={
          <Menu
            onSelect={handleMenuSelection}
            selectedItem={selectedItem}
            isCollapsed={isMenuCollapsed}
            onToggleCollapse={toggleMenuCollapse}
          />
        }
      >
        <SaListPageShell topBarTitle="Project Management">
          <ContentLoader label="Loading companies…" />
        </SaListPageShell>
      </DashboardShell>
    );
  }

  const selectedCompany = companies.find((company) => company.id === visibleSection);
  const searchNeedle = searchTerm.trim().toLowerCase();
  const companyDisplayName = (company) => company?.companyName || "";
  const projectDisplayName = (project) => project?.projectName || "";
  const companyMatchesSearch = (company) =>
    !searchNeedle || companyDisplayName(company).toLowerCase().includes(searchNeedle);
  const projectMatchesSearch = (project) =>
    !searchNeedle || projectDisplayName(project).toLowerCase().includes(searchNeedle);

  const displayedCompanies = sortTableRows(
    companies.filter((company) => {
      if (company.id === visibleSection || companyMatchesSearch(company)) return true;
      return (projects[company.id] || []).some(projectMatchesSearch);
    }),
    sortKey,
    {
      name: companyDisplayName,
      date: (company) => {
        const timestamp = Date.parse(company.createdDate || company.CreatedDate || "");
        return Number.isNaN(timestamp) ? Number(company.id || 0) : timestamp;
      },
    }
  );

  const displayedProjects = sortTableRows(
    getProjectsForCompany(visibleSection).filter(
      (project) => companyMatchesSearch(selectedCompany) || projectMatchesSearch(project)
    ),
    sortKey,
    {
      name: projectDisplayName,
      date: (project) => {
        const timestamp = Date.parse(project.projectCreationDate || project.creationDate || "");
        return Number.isNaN(timestamp) ? Number(project.id || 0) : timestamp;
      },
    }
  );

  const companySelectOptions = companies.map((company) => ({
    value: String(company.id),
    label: company.companyName,
  }));

  // Keep an existing project's department selectable even if it is no longer active.
  const departmentSelectOptions = [
    ...departmentNames,
    ...(formData.departmentName && !departmentNames.includes(formData.departmentName)
      ? [formData.departmentName]
      : []),
  ].map((name) => ({ value: name, label: name }));

  const renderProjectsPanel = () => {
    if (!visibleSection) {
      return (
        <div className="pm-placeholder">
          <p>Select a company to view and manage its projects.</p>
        </div>
      );
    }

    if (loadingProjects[visibleSection]) {
      return (
        <ContentLoader
          compact
          label={`Loading projects for ${selectedCompany?.companyName || "company"}…`}
        />
      );
    }

    const companyProjects = displayedProjects;

    if (companyProjects.length === 0) {
      return (
        <div className="pm-empty-state">
          <p>
            {searchNeedle ? (
              `No matching projects for ${selectedCompany?.companyName || "this company"}.`
            ) : (
              <>
                No projects yet for <strong>{selectedCompany?.companyName}</strong>.
              </>
            )}
          </p>
          {!searchNeedle && (
            <Button
              variant="primary"
              size="sm"
              className="btn btn-dashboard-primary"
              onClick={openModal}
            >
              <Plus className="me-1" />
              Create first project
            </Button>
          )}
        </div>
      );
    }

    return (
      <div className="pm-project-list">
        {companyProjects.map((project) => {
          const projectSmes = getProjectSmeUsers(project);
          const hasAssessment = projectHasAssessmentBlueprint(project);

          return (
            <article
              key={project.id}
              className={`pm-project-card rdm-card-clickable${
                hasAssessment ? "" : " pm-project-card--awaiting-assessment"
              }`}
              onClick={() => handleProjectClick(project)}
            >
              <div className="pm-project-card__header">
                <h3 className="pm-project-card__title">{project.projectName}</h3>
                <div
                  className="btn-list-actions pm-project-card__icon-actions"
                  onClick={(event) => event.stopPropagation()}
                >
                  <Button
                    variant="outline"
                    size="sm"
                    className="btn-list-edit"
                    title="Edit project"
                    aria-label={`Edit ${project.projectName}`}
                    onClick={(event) => openEditModal(project, event)}
                  >
                    <Pencil />
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    className="btn-list-delete"
                    title="Delete project"
                    aria-label={`Delete ${project.projectName}`}
                    onClick={(event) => openDeleteModal(project, event)}
                  >
                    <Trash2 />
                  </Button>
                </div>
              </div>

              <dl className="pm-project-card__meta">
                <div className="pm-project-card__meta-row">
                  <dt>Reference</dt>
                  <dd>{project.projectNumber || "—"}</dd>
                </div>
                <div className="pm-project-card__meta-row">
                  <dt>Department</dt>
                  <dd>{project.departmentName}</dd>
                </div>
                <div className="pm-project-card__meta-row">
                  <dt>Created</dt>
                  <dd>
                    {project.projectCreationDate
                      ? new Date(project.projectCreationDate).toLocaleDateString()
                      : "—"}
                  </dd>
                </div>
                <div className="pm-project-card__meta-row pm-project-card__meta-row--assessment">
                  <dt>Assessment</dt>
                  <dd className={hasAssessment ? undefined : "pm-project-card__assessment-unassigned"}>
                    {getProjectAssessmentBlueprintTitle(project) || "Not assigned"}
                  </dd>
                </div>
              </dl>

              {projectSmes.length > 0 ? (
                <div className="pm-sme-block">
                  <span className="pm-sme-block__label">Assigned SMEs</span>
                  <span className="pm-sme-block__names">
                    {projectSmes.map(getSmeDisplayName).join(", ")}
                  </span>
                </div>
              ) : null}

              <div className="pm-project-card__actions">
                <Button
                  variant={hasAssessment ? "primary" : "outline"}
                  size="sm"
                  className={hasAssessment ? "btn btn-dashboard-primary" : "pm-project-card__invites-disabled"}
                  disabled={!hasAssessment}
                  onClick={(event) => openInvitations(project, event)}
                  title={
                    hasAssessment
                      ? "Manage user invitations"
                      : "Publish an assessment to this project before inviting users"
                  }
                >
                  Manage user invitations
                </Button>
                {!hasAssessment ? (
                  <p className="pm-project-card__invites-hint">
                    Assign an assessment from Assessment Assignments before sending invites.
                  </p>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <DashboardShell
        isMenuCollapsed={isMenuCollapsed}
        sidebar={
          <Menu
            onSelect={handleMenuSelection}
            selectedItem={selectedItem}
            isCollapsed={isMenuCollapsed}
            onToggleCollapse={toggleMenuCollapse}
          />
        }
      >
        <SaListPageShell topBarTitle="Project Management">
          <div className="dash-frame-parent1 pm-project-management">
            <div className="dash-list-toolbar">
              <div className="dash-list-toolbar__intro">
                <h5 className="dash-list-toolbar__title">Manage Projects</h5>
                <p className="dash-list-toolbar__desc">
                  Create projects for companies and manage user invitations and SME assignments.
                </p>
              </div>
              <div className="dash-list-toolbar__actions">
                <div className="dash-search-field dash-table-search">
                  <Search className="dash-search-field__icon" aria-hidden />
                  <input
                    type="text"
                    placeholder="Search company or project…"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    className="dash-search-field__input"
                  />
                </div>
                <TableSortSelect variant="chip" value={sortKey} onChange={setSortKey} />
                <Button variant="primary" className="sa-create-btn" onClick={openModal}>
                  Create Project
                </Button>
              </div>
            </div>

            <div className="pm-layout">
              <aside className="pm-panel pm-panel--companies">
                <h2 className="pm-panel__title">Companies</h2>
                <div className="pm-list">
                  {displayedCompanies.length === 0 ? (
                    <div className="pm-empty-state pm-empty-state--compact">
                      <p>No companies found.</p>
                    </div>
                  ) : (
                    displayedCompanies.map((company) => {
                      const isSelected = selectedCard === company.id;
                      const isOpen = visibleSection === company.id;

                      return (
                        <div
                          key={company.id}
                          className={`pm-list-card ${
                            isSelected ? "pm-list-card--selected" : ""
                          }`}
                          onClick={() => toggleVisibility(company.id)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              toggleVisibility(company.id);
                            }
                          }}
                        >
                          <div className="pm-list-card__body">
                            <div className="pm-list-card__title">{company.companyName}</div>
                            {company.industry ? (
                              <div className="pm-list-card__subtitle">{company.industry}</div>
                            ) : null}
                            {company.companyInfo ? (
                              <div className="pm-list-card__subtitle">{company.companyInfo}</div>
                            ) : null}
                          </div>
                          {isOpen ? (
                            <ChevronDown className="pm-chevron-icon" aria-hidden />
                          ) : (
                            <ChevronRight className="pm-chevron-icon" aria-hidden />
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </aside>

              <section className="pm-panel pm-panel--projects">
                <h2 className="pm-panel__title">
                  {selectedCompany
                    ? `Projects — ${selectedCompany.companyName}`
                    : "Projects"}
                </h2>
                {renderProjectsPanel()}
              </section>
            </div>
          </div>
        </SaListPageShell>
      </DashboardShell>

      <Modal
        open={showModal}
        onClose={closeModal}
        size="lg"
        className="pm-project-form-modal"
        title={
          <span className="inline-flex items-center gap-2">
            <FolderOpen className="h-5 w-5" />
            {isEditMode ? "Edit Project" : "Add New Project"}
          </span>
        }
        footer={
          <>
            <Button variant="outline" onClick={closeModal} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="project-form"
              variant="primary"
              isLoading={isSubmitting}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? isEditMode
                  ? "Updating…"
                  : "Creating…"
                : isEditMode
                  ? "Update Project"
                  : "Create Project"}
            </Button>
          </>
        }
      >
        <form id="project-form" onSubmit={handleSubmit} className="pm-project-form">
          <div className="pm-project-form__block">
            <div className="pm-project-form__section-label">Project Details</div>
            <div className="pm-project-form__grid">
              <Select
                label="Company"
                name="companyId"
                value={String(formData.companyId || "")}
                onChange={(value) => setFormField("companyId", value)}
                options={companySelectOptions}
                placeholder="Select a company"
                disabled={isEditMode}
                required
                error={formErrors.companyId}
              />
              <Input
                label="Project Name"
                name="projectName"
                value={formData.projectName}
                onChange={handleInputChange}
                placeholder="Enter project name"
                required
                error={formErrors.projectName}
              />
              {!loadingDepartments && departmentNames.length === 0 ? (
                <Input
                  label="Department Name"
                  name="departmentName"
                  value={formData.departmentName}
                  onChange={handleInputChange}
                  placeholder="Enter department name"
                  required
                  error={formErrors.departmentName}
                />
              ) : (
                <Select
                  label="Department"
                  name="departmentName"
                  value={formData.departmentName}
                  onChange={(value) => setFormField("departmentName", value)}
                  options={departmentSelectOptions}
                  placeholder={loadingDepartments ? "Loading departments…" : "Select a department"}
                  disabled={loadingDepartments}
                  searchable
                  required
                  error={formErrors.departmentName}
                />
              )}
              <Input
                label="Project Creation Date"
                type="date"
                name="projectCreationDate"
                value={formData.projectCreationDate}
                onChange={handleInputChange}
                required
                error={formErrors.projectCreationDate}
              />
              {isEditMode && editingProject?.projectNumber ? (
                <Input
                  label="Project Reference"
                  name="projectReference"
                  value={editingProject.projectNumber}
                  readOnly
                  disabled
                />
              ) : null}
            </div>
          </div>

          <div className="pm-project-form__block pm-project-form__block--sme">
            <div className="pm-project-form__section-head">
              <div className="pm-project-form__section-label">Assign SMEs</div>
              <span className="pm-project-form__count" aria-live="polite">
                {formData.smeUserIds.length} selected
              </span>
            </div>
            <p className="pm-project-form__hint">
              Check SMEs to assign them to this project. Uncheck to revoke.
              {isEditMode && !projectHasAssessmentBlueprint(editingProject)
                ? " A published assessment is required before SMEs can be assigned."
                : ""}
            </p>
            {formErrors.smeUserIds ? (
              <p className="app-field__error mb-2">{formErrors.smeUserIds}</p>
            ) : null}
            {loadingSMEs ? (
              <div className="pm-sme-checkbox-list__loading">Loading SMEs…</div>
            ) : smes.length === 0 ? (
              <div className="pm-sme-checkbox-list__empty">No SMEs available.</div>
            ) : (
              <>
                <div className="pm-sme-search">
                  <Search className="pm-sme-search__icon" aria-hidden />
                  <input
                    type="text"
                    className="app-input pm-sme-search__input"
                    placeholder="Search SMEs…"
                    value={smeSearch}
                    onChange={(e) => setSmeSearch(e.target.value)}
                    aria-label="Search SMEs"
                    autoComplete="off"
                  />
                </div>
                <div className="pm-sme-checkbox-list">
                {filteredSmes.length === 0 ? (
                  <div className="pm-sme-checkbox-list__empty">No SMEs match your search.</div>
                ) : null}
                {filteredSmes.map((sme) => (
                  <Checkbox
                    key={sme.id}
                    id={`project-sme-${sme.id}`}
                    className="pm-sme-checkbox-item"
                    label={sme.designation ? `${sme.name} — ${sme.designation}` : sme.name}
                    checked={isSmeSelected(sme.id)}
                    onChange={(e) => handleSmeToggle(sme.id, e.target.checked)}
                  />
                ))}
                </div>
              </>
            )}
          </div>
        </form>
      </Modal>

      {invitationProject ? (
        <ProjectInvitationsPanel
          projectId={invitationProject.id}
          projectName={invitationProject.projectName}
          assessmentBlueprintId={getProjectAssessmentBlueprintId(invitationProject)}
          show={showInvitationModal}
          onHide={() => {
            setShowInvitationModal(false);
            setInvitationProject(null);
          }}
        />
      ) : null}

      <Modal
        open={showDeleteModal}
        onClose={() => !isDeleting && cancelDelete()}
        title="Confirm Delete"
        footer={
          <>
            <Button variant="outline" onClick={cancelDelete} disabled={isDeleting}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={confirmDelete}
              isLoading={isDeleting}
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting…" : "Delete Project"}
            </Button>
          </>
        }
      >
        <p className="m-0 text-[var(--app-text)]">
          Are you sure you want to permanently delete the project &quot;
          {projectToDelete?.projectName}&quot;?
        </p>
        <p className="mt-3 mb-0 text-[var(--app-danger)]">
          This cannot be undone. All assessments, chats, reports, invitations, and memberships for
          this project will be deleted. SuperUser (team lead) accounts assigned to this project will
          also be deleted. Client users are kept until the company is deleted.
        </p>
        {projectToDelete && projectHasAssessmentBlueprint(projectToDelete) ? (
          <p className="mt-3 mb-0 text-[var(--app-text-muted)]">
            The linked assessment (
            {getProjectAssessmentBlueprintTitle(projectToDelete) || "assigned assessment"}) will be
            unassigned from this project and set back to <strong>Draft</strong> so it can be published
            to another project later. Assessment content is not deleted.
          </p>
        ) : null}
      </Modal>

      <RecordDetailModal
        show={showViewModal}
        onHide={closeViewModal}
        title={viewProject?.projectName || "Project Details"}
        subtitle={
          viewProject?.projectNumber
            ? `Reference: ${viewProject.projectNumber}`
            : "Project record"
        }
        fields={[
          { label: "Project Name", value: viewProject?.projectName },
          { label: "Project Number", value: viewProject?.projectNumber },
          { label: "Department", value: viewProject?.departmentName },
          {
            label: "Created",
            value: viewProject?.projectCreationDate
              ? new Date(viewProject.projectCreationDate).toLocaleDateString()
              : undefined,
          },
          {
            label: "Assessment",
            value: getProjectAssessmentBlueprintTitle(viewProject) || "Not assigned",
          },
          {
            label: "Assigned SMEs",
            value: (() => {
              const assigned = getProjectSmeUsers(viewProject);
              return assigned.length > 0
                ? assigned.map(getSmeDisplayName).join(", ")
                : undefined;
            })(),
            fullWidth: true,
          },
        ]}
        actions={[
          {
            type: "edit",
            onClick: () => {
              const project = viewProject;
              closeViewModal();
              if (project) openEditModal(project, syntheticStopEvent);
            },
          },
          {
            type: "delete",
            onClick: () => {
              const project = viewProject;
              closeViewModal();
              if (project) openDeleteModal(project, syntheticStopEvent);
            },
          },
        ]}
      />
    </>
  );
};

export default ProjectManagement;
